/**
 * B963 — where the keyboard lands when a submit is refused.
 *
 * Every form in the product says its refusals at the field (FORMS.md R18:
 * the error under the control, `aria-invalid` on it). But the keyboard stayed
 * on the pressed door: on the membership checkout (dev, 2026-10-21) the next
 * Tab after a refused CONTINUE TO PAYMENT went to BACK TO PLANS and then to
 * `<body>`, and a screen reader said nothing — the buyer had to hunt
 * backwards for what was refused.
 *
 * One rule, installed once per app at its entry: after a form's submit, once
 * the render has settled (React commits a discrete event's update before a
 * zero timeout fires), when the keyboard is still where the press left it —
 * on the pressed door, or lost — and the form now holds a shown, enabled
 * `[aria-invalid="true"]` control, the focus moves to the FIRST one in
 * document order. A focus the app moved itself (a sheet opening, a view
 * swapping) is never taken, and an accepted submit leaves no refusal to find.
 */
import { focusIsLost } from "./viewFocus.js";

const REFUSED = '[aria-invalid="true"]';

const shown = (el: HTMLElement): boolean => {
  if (el.closest("[hidden], [aria-hidden='true'], [inert]") !== null) return false;
  const check = (el as HTMLElement & { checkVisibility?: () => boolean }).checkVisibility;
  return typeof check === "function" ? check.call(el) : true;
};

/** The first refused control under `root` the keyboard can stand on, or undefined. */
export function firstRefusal(root: ParentNode): HTMLElement | undefined {
  return Array.from(root.querySelectorAll<HTMLElement>(REFUSED)).find(
    (el) => shown(el) && !(el as HTMLElement & { disabled?: boolean }).disabled,
  );
}

/**
 * B993 — the same rule for a press that is NOT a form submit (the cart's
 * Pay door lives in the sticky bar, outside the buyer fields' form, so the
 * submit listener never hears it): read where the keyboard is NOW, and once
 * the refusal has rendered move it to the first refused control under
 * `root` — unless the app moved it itself.
 */
export function focusRefusalSoon(doc: Document, root: ParentNode & { isConnected?: boolean }, later: (run: () => void) => void = (run) => void setTimeout(run, 0)): void {
  const pressed = doc.activeElement;
  later(() => {
    if (root.isConnected === false) return;
    if (doc.activeElement !== pressed && !focusIsLost(doc)) return;
    firstRefusal(root)?.focus();
  });
}

/**
 * Listen for submits on `doc`; returns the uninstaller. `later` defers the
 * look until the refusal has rendered (a zero timeout by default).
 */
export function installRefusalFocus(doc: Document, later: (run: () => void) => void = (run) => void setTimeout(run, 0)): () => void {
  const onSubmit = (e: Event): void => {
    const form = e.target as Element | null;
    if (form === null || form.tagName !== "FORM") return;
    focusRefusalSoon(doc, form, later);
  };
  // Capture: the pressed door is read before any app handler can move the focus.
  doc.addEventListener("submit", onSubmit, true);
  return () => doc.removeEventListener("submit", onSubmit, true);
}
